import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Copy, ExternalLink, KeyRound, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type ExtraField = { key: string; label: string; help: string; required: boolean };

type Setup = {
  console_label: string;
  console_url: string;
  steps: string[];
  note: string;
};

type Provider = {
  alias: string;
  display_name: string;
  extra_fields: ExtraField[];
  configured: boolean;
  enabled: boolean;
  client_id: string;
  has_secret: boolean;
  issuer: string;
  status_known: boolean;
  redirect_uri: string;
  setup: Setup;
};

type KeycloakProblem = {
  title: string;
  explanation: string;
  steps: string[];
};

function apiError(error: unknown, fallback: string) {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}

function ProblemCard({ problem }: { problem: KeycloakProblem }) {
  return (
    <div
      className="rounded-md border border-amber-500/50 bg-amber-50 p-4 text-sm text-amber-950"
      role="alert"
      data-testid="idp-problem"
    >
      <p className="flex items-center gap-2 font-semibold">
        <AlertTriangle className="h-4 w-4" />
        {problem.title}
      </p>
      <p className="mt-1">{problem.explanation}</p>
      <ol className="mt-3 list-decimal space-y-1 pl-5">
        {problem.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </div>
  );
}

function RedirectUri({ value }: { value: string }) {
  return (
    <div className="space-y-1">
      <Label>Redirect URI</Label>
      <div className="flex gap-2">
        <code className="block flex-1 overflow-x-auto rounded border bg-muted/40 px-3 py-2 text-xs">
          {value}
        </code>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            navigator.clipboard
              .writeText(value)
              .then(() => toast.success('Redirect URI copied.'))
              .catch(() => toast.error('Could not copy it. Select it and copy it by hand.'))
          }
        >
          <Copy className="mr-1 h-3.5 w-3.5" />
          Copy
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Register exactly this address with the provider as the redirect URI.
      </p>
    </div>
  );
}

function SetupSteps({ provider }: { provider: Provider }) {
  const [open, setOpen] = React.useState(!provider.configured);
  const { setup } = provider;
  return (
    <div className="rounded-md border bg-muted/20">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`idp-setup-toggle-${provider.alias}`}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        How to get the client ID and client secret
      </button>
      {open && (
        <div className="space-y-3 px-3 pb-3 text-sm" data-testid={`idp-setup-${provider.alias}`}>
          {setup.console_url && (
            <a
              href={setup.console_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary underline"
            >
              Open the {setup.console_label}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          <ol className="list-decimal space-y-1.5 pl-5">
            {setup.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          {setup.note && <p className="text-xs text-muted-foreground">{setup.note}</p>}
        </div>
      )}
    </div>
  );
}

function ProviderCard({ provider }: { provider: Provider }) {
  const queryClient = useQueryClient();
  const [clientId, setClientId] = React.useState(provider.client_id);
  const [secret, setSecret] = React.useState('');
  const [extra, setExtra] = React.useState<Record<string, string>>({});
  const [enabled, setEnabled] = React.useState(provider.enabled);

  React.useEffect(() => {
    setClientId(provider.client_id);
    setEnabled(provider.enabled);
  }, [provider.client_id, provider.enabled]);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['identity-providers'] });

  const save = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.put('/global-admin/identity-providers/', {
        alias: provider.alias,
        client_id: clientId.trim(),
        client_secret: secret.trim(),
        enabled,
        extra,
      });
      return res.data;
    },
    onSuccess: () => {
      setSecret('');
      refresh();
      toast.success(`${provider.display_name} saved.`);
    },
    onError: (error) => toast.error(apiError(error, 'Keycloak would not take that.')),
  });

  const remove = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete('/global-admin/identity-providers/', {
        params: { alias: provider.alias },
      });
    },
    onSuccess: () => {
      refresh();
      toast.success(`${provider.display_name} removed.`);
    },
    onError: () => toast.error('Could not remove that provider.'),
  });

  const missingExtra = provider.extra_fields.some(
    (field) => field.required && !provider.configured && !extra[field.key]?.trim(),
  );
  const canSave =
    clientId.trim().length > 0 &&
    (secret.trim().length > 0 || provider.has_secret) &&
    !missingExtra;

  return (
    <Card data-testid={`idp-card-${provider.alias}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">{provider.display_name}</CardTitle>
          {!provider.status_known ? (
            <Badge variant="outline" data-testid={`idp-state-${provider.alias}`}>
              Status unknown
            </Badge>
          ) : provider.configured ? (
            <Badge
              variant="secondary"
              className={
                provider.enabled
                  ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'
                  : ''
              }
              data-testid={`idp-state-${provider.alias}`}
            >
              {provider.enabled ? (
                <>
                  <Check className="mr-1 h-3 w-3" />
                  On
                </>
              ) : (
                'Configured, off'
              )}
            </Badge>
          ) : (
            <Badge variant="outline" data-testid={`idp-state-${provider.alias}`}>
              Not set up
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Label htmlFor={`on-${provider.alias}`} className="text-xs">
              Offer it
            </Label>
            <Switch
              id={`on-${provider.alias}`}
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid={`idp-toggle-${provider.alias}`}
            />
          </div>
        </div>
        <CardDescription>
          {provider.issuer
            ? provider.issuer
            : `Register ${provider.display_name} as an OIDC app, then paste its credentials here.`}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        <RedirectUri value={provider.redirect_uri} />
        <SetupSteps provider={provider} />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`cid-${provider.alias}`}>Client ID</Label>
            <Input
              id={`cid-${provider.alias}`}
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              data-testid={`idp-client-id-${provider.alias}`}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`sec-${provider.alias}`}>Client secret</Label>
            <Input
              id={`sec-${provider.alias}`}
              type="password"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              placeholder={
                provider.has_secret ? 'Set — leave blank to keep it' : 'Required'
              }
              data-testid={`idp-secret-${provider.alias}`}
            />
          </div>
        </div>

        {provider.extra_fields.map((field) => (
          <div key={field.key} className="space-y-1">
            <Label htmlFor={`${provider.alias}-${field.key}`}>{field.label}</Label>
            <Input
              id={`${provider.alias}-${field.key}`}
              value={extra[field.key] ?? ''}
              onChange={(event) =>
                setExtra({ ...extra, [field.key]: event.target.value })
              }
              placeholder={provider.configured ? 'Leave blank to keep it' : ''}
              data-testid={`idp-${field.key}-${provider.alias}`}
            />
            <p className="text-xs text-muted-foreground">{field.help}</p>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!canSave || save.isPending}
            onClick={() => save.mutate()}
            data-testid={`idp-save-${provider.alias}`}
          >
            {save.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
          {provider.configured && (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
              data-testid={`idp-remove-${provider.alias}`}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SignInProvidersPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['identity-providers'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/identity-providers/');
      return res.data as { providers: Provider[]; problem: KeycloakProblem | null };
    },
  });

  return (
    <div className="space-y-6 p-6" data-testid="sign-in-providers-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <KeyRound className="h-6 w-6" />
          Sign-in providers
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Let people sign up and sign in with an account they already have.
          What comes out the other side is an ordinary account in the realm —
          same groups, same token, administered the same way — so nothing else
          in BitSwan has to know which door somebody came through.
        </p>
      </div>

      {data?.problem && <ProblemCard problem={data.problem} />}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div
          className="rounded-md border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-700"
          role="alert"
          data-testid="idp-load-error"
        >
          {apiError(error, 'The sign-in providers could not be loaded. Reload the page to try again.')}
        </div>
      ) : (data?.providers ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="idp-none">
          This AOC offers no sign-in providers.
        </p>
      ) : (
        <div className="space-y-4">
          {(data?.providers ?? []).map((provider) => (
            <ProviderCard key={provider.alias} provider={provider} />
          ))}
        </div>
      )}
    </div>
  );
}
